import { useEffect, useRef } from 'react';
import { Box, Skeleton, Typography, Card } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { observer, useLocalObservable } from 'mobx-react-lite';
import './scss/dashboards-data.style.scss';
import i18n from '../../../i18n';
import DataListItem from './data-list-item';
import { CurrentData } from '../../../models/abstract/current-data.entity';
import { formatEUR } from '../../../constants/tool.fn';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import DashboardsDataStore from './dashboards-data-state';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DineIcon } from '../../../static/images/svg/free_breakfast.svg';
import { ReactComponent as DeliveryIcon } from '../../../static/images/svg/ph_handbag-fill.svg';
import { ReactComponent as DishIcon } from '../../../static/images/svg/material-symbols_room-service-rounded.svg';
import { ReactComponent as MoneyIcon } from '../../../static/images/svg/ri_coins-fill.svg';
import CurrentStatusAndRiskCard from './current-status-and-risk.card';
import TimePeriodFilter, { TIME_PERIOD_FILTER_WIDTH_PX, TimePeriodValue } from './time-period-filter';
import type { CurrentBusinessDataType } from '../../../models/abstract/current-business-data.entity';

/** 时间段筛选 UI 值与接口 type 参数对应关系 */
const PERIOD_TO_TYPE: Record<TimePeriodValue, CurrentBusinessDataType> = {
    all_day: '0',
    lunch: '1',
    dinner: '2',
};

/** 接口 type 与顶部筛选值的反向映射 */
const TYPE_TO_PERIOD: Record<CurrentBusinessDataType, TimePeriodValue> = {
    '0': 'all_day',
    '1': 'lunch',
    '2': 'dinner',
};

/** 营业状态 + 已结/未结分析：桌面端（md 及以上）主卡片固定宽度（px），与稿一致 */
const DASHBOARDS_DATA_CARD_WIDTH_DESKTOP_PX = 400;
/** 三主卡网格间距（px） */
const DASHBOARDS_DATA_MAIN_CARDS_GAP_PX = 24;
/** 三主卡同排所需视口最小宽度：3×卡宽 + 2×间距（与原先 auto-fill 放不下第三张的临界点一致） */
const DASHBOARDS_DATA_THREE_COL_MIN_WIDTH_PX =
    DASHBOARDS_DATA_CARD_WIDTH_DESKTOP_PX * 3 + DASHBOARDS_DATA_MAIN_CARDS_GAP_PX * 2;

/**
 * 看板数据页主卡片区布局：小屏主卡宽度 100% 避免固定 400px 横向溢出，大屏保持固定宽。
 * @returns analysisCardSx - 分析主 Card 宽度相关 sx；leftColumnOuterSx - 左卡外层（当前营业+风控）；mainCardsRowSx - 三主卡横排行
 */
function useDashboardsDataResponsiveLayout() {
    const theme = useTheme();
    /** 是否达到主题 md 断点（默认 960px） */
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    /** 视口是否足以一行并排三主卡（400×3 + 间距），不足则单列逐行，不出现一行两张 */
    const fitsThreeMainCardsInRow = useMediaQuery(
        `(min-width: ${DASHBOARDS_DATA_THREE_COL_MIN_WIDTH_PX}px)`,
    );
    /** 主卡片区域宽度：大屏固定 px，小屏占满行 */
    const mainCardWidth = isMdUp ? DASHBOARDS_DATA_CARD_WIDTH_DESKTOP_PX : '100%';
    /** 已结/未结分析主 Card */
    const analysisCardSx = {
        width: mainCardWidth,
        flexShrink: 0,
        boxSizing: 'border-box' as const,
        ...(!isMdUp ? { maxWidth: '100%', minWidth: 0 } : {}),
    };
    /** 左侧当前营业 + 风控卡外层 */
    const leftColumnOuterSx = {
        width: mainCardWidth,
        flexShrink: 0,
        boxSizing: 'border-box' as const,
        ...(!isMdUp ? { maxWidth: '100%', minWidth: 0 } : {}),
    };
    /**
     * 时段筛选下方三主卡：仅两种排布——一行 3 张，或单列逐张（不出现一行 2 张）。
     * 换行临界点与原先「第三张放不下」一致（约 1248px 视口宽）；md 以下小屏单列满宽。
     */
    const mainCardsRowSx = {
        display: 'grid',
        gridTemplateColumns:
            isMdUp && fitsThreeMainCardsInRow
                ? `repeat(3, ${DASHBOARDS_DATA_CARD_WIDTH_DESKTOP_PX}px)`
                : '1fr',
        gap: `${DASHBOARDS_DATA_MAIN_CARDS_GAP_PX}px`,
        width: '100%',
        alignItems: 'start' as const,
    };
    return { analysisCardSx, leftColumnOuterSx, mainCardsRowSx };
}

/**
 * 午/晚餐人数行左侧文案对应的 i18n key，与接口/筛选 type 一致。
 * @param type - '0' 全天，'1' 午餐，'2' 晚餐
 */
function getTimeCustomerNumLabelKey(type: CurrentBusinessDataType): string {
    if (type === '1') {
        return 'business_analysis_page_text_lunch_customer_num';
    }
    if (type === '2') {
        return 'business_analysis_page_text_dinner_customer_num';
    }
    return 'business_analysis_page_text_finish_time_customer_num';
}

/** 与 `DataListItem` 行分隔样式一致 */
const SKELETON_LIST_SEPARATOR_SX = {
    '& > *:not(:last-child)': {
        borderBottom: '1px solid var(--stroke-neutral-800-a25)',
        paddingBottom: '8px',
        marginBottom: '8px',
    },
} as const;

type DashboardDataRowSkeletonProps = {
    /** 是否模拟粗体汇总行（略高） */
    bold?: boolean;
    /** 左侧文案条占宽 */
    labelWidth: number | string;
    /** 是否展示左侧圆标（拟合 icon） */
    withIcon?: boolean;
};

/**
 * 数据分析列表行骨架：与 `DataListItem` 布局一致（左文 optional 图标、右数字）。
 */
function DashboardDataRowSkeleton({ bold, labelWidth, withIcon = true }: DashboardDataRowSkeletonProps) {
    const h = bold ? 24 : 20;
    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
            <Box display="flex" alignItems="center" flex={1} minWidth={0}>
                {withIcon && <Skeleton variant="circular" width={24} height={24} animation="wave" sx={{ mr: 1, flexShrink: 0 }} />}
                <Skeleton variant="rounded" animation="wave" width={labelWidth} height={h} sx={{ borderRadius: '4px' }} />
            </Box>
            <Skeleton variant="rounded" width={72} height={h} animation="wave" sx={{ borderRadius: '4px' }} />
        </Box>
    );
}

type InnerSectionCardSkeletonProps = {
    /** 子区块内列表行数（堂食 / 外卖 / 菜品等） */
    rowCount: number;
};

/**
 * 分析主卡片内子卡片骨架：居中「图标 + 小标题」+ 多行 key-value。
 */
function InnerSectionCardSkeleton({ rowCount }: InnerSectionCardSkeletonProps) {
    return (
        <Card
            sx={{
                padding: '16px 8px',
                borderRadius: '16px',
            }}
        >
            <Box sx={{ mt: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 1 }}>
                    <Skeleton variant="circular" width={24} height={24} animation="wave" />
                    <Skeleton variant="rounded" width={110} height={22} animation="wave" sx={{ borderRadius: '4px' }} />
                </Box>
                <Box sx={SKELETON_LIST_SEPARATOR_SX}>
                    {Array.from({ length: rowCount }, (_, i) => (
                        <DashboardDataRowSkeleton key={i} labelWidth={`${48 + (i % 3) * 8}%`} withIcon={false} />
                    ))}
                </Box>
            </Box>
        </Card>
    );
}

type AnalysisMainCardSkeletonProps = {
    /** 标题条宽度（两卡略有差异更自然） */
    titleWidth: number;
    /** 顶部汇总行数：已结实收+折扣 2 行；未结应收必有，已付仅后端返回时多 1 行 */
    totalRowCount: 1 | 2;
    /** 三个子区块各自的列表行数，与实页 `cardArr` 各 section 的 `items.length` 一致 */
    sectionRowCounts: readonly [number, number, number];
    /** 主卡片宽度：大屏 px、小屏 100%，与实页一致 */
    cardWidth: number | string;
};

/**
 * 已结/未结分析主卡片骨架：标题 + 汇总行 + 三个子区块。
 */
function AnalysisMainCardSkeleton({ titleWidth, totalRowCount, sectionRowCounts, cardWidth }: AnalysisMainCardSkeletonProps) {
    return (
        <Card
            sx={{
                padding: '24px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                borderRadius: '16px',
                width: cardWidth,
                flexShrink: 0,
                boxSizing: 'border-box',
                ...(typeof cardWidth === 'string' ? { maxWidth: '100%', minWidth: 0 } : {}),
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Skeleton variant="rounded" width={titleWidth} height={32} animation="wave" sx={{ borderRadius: '8px' }} />
            </Box>
            <Box sx={SKELETON_LIST_SEPARATOR_SX}>
                {totalRowCount === 2 && (
                    <>
                        <DashboardDataRowSkeleton bold labelWidth="72%" withIcon />
                        <DashboardDataRowSkeleton bold labelWidth="68%" withIcon />
                    </>
                )}
                {totalRowCount === 1 && <DashboardDataRowSkeleton bold labelWidth="70%" withIcon />}
            </Box>
            {sectionRowCounts.map((rowCount, idx) => (
                <InnerSectionCardSkeleton key={idx} rowCount={rowCount} />
            ))}
        </Card>
    );
}

/**
 * 看板整页加载骨架：布局与实页一致（左当前营业、右筛选 + 双列分析卡），细粒度块而非大块矩形。
 */
function DashboardsDataPageSkeleton() {
    const { leftColumnOuterSx, mainCardsRowSx } = useDashboardsDataResponsiveLayout();
    /** 与实页分析卡同宽，供骨架屏双主卡使用 */
    const skeletonMainCardWidth = leftColumnOuterSx.width as number | string;
    return (
        <Box
            display="flex"
            flexWrap="wrap"
            gap="24px"
            flexDirection="column"
            sx={{
                marginTop: '24px',
                paddingBottom: '24px',
            }}
            aria-busy="true"
            aria-label={`${i18n.t('business_analysis_page_text_immediate_info')}, ${i18n.t('general_text_loading')}`}
        >
            <Box display="flex" flexDirection="column" gap="16px" alignItems="flex-start" width="100%">
                <Box sx={{ width: TIME_PERIOD_FILTER_WIDTH_PX, maxWidth: '100%', boxSizing: 'border-box' }}>
                    <Skeleton variant="rounded" width="100%" height={40} animation="wave" sx={{ borderRadius: '8px' }} />
                </Box>
                <Box sx={mainCardsRowSx}>
                    <Box sx={leftColumnOuterSx}>
                        <Card
                            sx={{
                                padding: '24px 16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                borderRadius: '16px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Skeleton variant="rounded" width="78%" height={32} animation="wave" sx={{ borderRadius: '8px', alignSelf: 'center' }} />
                            <Box
                                sx={{
                                    border: '1px solid var(--stroke-neutral-800-a25)',
                                    padding: '24px 8px',
                                    borderRadius: '16px',
                                }}
                            >
                                <Box sx={SKELETON_LIST_SEPARATOR_SX}>
                                    {[72, 68, 64, 70].map((lw, i) => (
                                        <DashboardDataRowSkeleton key={i} labelWidth={`${lw}%`} withIcon />
                                    ))}
                                </Box>
                            </Box>
                        </Card>
                    </Box>
                    <AnalysisMainCardSkeleton
                        titleWidth={220}
                        totalRowCount={2}
                        sectionRowCounts={[4, 2, 9] as const}
                        cardWidth={skeletonMainCardWidth}
                    />
                    <AnalysisMainCardSkeleton
                        titleWidth={200}
                        totalRowCount={1}
                        sectionRowCounts={[4, 2, 4] as const}
                        cardWidth={skeletonMainCardWidth}
                    />
                </Box>
            </Box>
        </Box>
    );
}

const DashboardsDataPage = observer(() => {
    useTranslation();
    const { analysisCardSx, leftColumnOuterSx, mainCardsRowSx } = useDashboardsDataResponsiveLayout();
    /** 与 `leftColumnOuterSx.width` 一致，供区块内骨架分析卡宽 */
    const analysisSkeletonCardWidth = leftColumnOuterSx.width as number | string;
    const interval = useRef<NodeJS.Timer | null>(null);
    /** 从主页面状态取当前餐厅，不依赖「当前营业状态」页是否打开 */
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const stringId = mainPageData.currentRestaurant?.string_id || '';
    const dashboardsDataStore = useLocalObservable(() => new DashboardsDataStore());

    /** 后端返回 paid_amount_total 时才展示「已付总额」（含 0）；缺字段不展示以兼容旧接口 */
    const showUnpaidPaidAmount = typeof dashboardsDataStore.data.unpaid_paid_amount === 'number';

    // 未结 - 统计
    const openListSource: CardItem[] = [
        {
            key: 'unpaid_original_amount',
            label: i18n.t('business_analysis_page_text_should_amount_received'),
            isPrice: true,
            icon: <MoneyIcon height={'24px'} color='var(--icon-neutral-500)' />,
        },
        ...(showUnpaidPaidAmount
            ? [
                  {
                      key: 'unpaid_paid_amount' as const,
                      label: i18n.t('business_analysis_page_text_paid_amount_total'),
                      isPrice: true,
                      icon: <MoneyIcon height={'24px'} color='var(--icon-neutral-500)' />,
                  },
              ]
            : []),
    ];
    // 未结 - 堂食
    const openDineInListSource: CardItem[] = [
        {
            key: 'using_table_num',
            label: i18n.t('business_analysis_page_text_dine_in_table_nums'),
        }, {
            key: 'sum_customer_num',
            label: i18n.t('business_analysis_page_text_dine_in_people_nums'),
        }, {
            key: 'time_customer_num',
            label: i18n.t(getTimeCustomerNumLabelKey(dashboardsDataStore.timePeriodType)),
        }, {
            key: 'dine_in_order_total',
            label: i18n.t('business_analysis_page_text_dine_in_orders_amount'),
            isPrice: true,
        }
    ]
    // 未结 - 外卖
    const openDeliveryListSource: CardItem[] = [
        {
            key: 'delivery_num',
            label: i18n.t('business_analysis_page_text_takeaways_orders_nums'),
        }, {
            key: 'takeaway_order_total',
            label: i18n.t('business_analysis_page_text_takeout_amount_total'),
            isPrice: true,
        },
    ]
    // 未结 - 菜品
    const openDishListSource: CardItem[] = [
        {
            key: 'total_dish',
            label: i18n.t('business_analysis_page_text_total_dish'),
        }, {
            key: 'rush_dish',
            label: i18n.t('business_analysis_page_text_rush_dish'),
        }, {
            key: 'cancelled_dish',
            label: i18n.t('business_analysis_page_text_cancelled_dish')
        }, {
            key: 'refund_dish_total',
            label: i18n.t('business_analysis_page_text_return_dish_amount'),
            isPrice: true,
        }
    ]

    // 已结订单数据源（数据格式）
    const closedListSource: CardItem[] = [
        {
            key: 'finish_actual_amount',
            label: i18n.t('business_analysis_page_text_actual_amount_total'),
            isPrice: true,
            icon: <MoneyIcon height={'24px'} color='var(--icon-neutral-500)' />,

        },
        {
            key: 'finish_discount_amount',
            label: i18n.t('business_analysis_page_text_discount_amount_total'),
            isPrice: true,
            icon: <MoneyIcon height={'24px'} color='var(--icon-neutral-500)' />,

        },

    ];

    // 已结 - 堂食
    const closedDineInListSource: CardItem[] = [
        {
            key: 'finish_table_num',
            label: i18n.t('business_analysis_page_text_dine_in_table_nums'),

        }, {
            key: 'finish_sum_customer_num',
            label: i18n.t('business_analysis_page_text_dine_in_people_nums'),

        }, {
            key: 'finish_time_customer_num',
            label: i18n.t(getTimeCustomerNumLabelKey(dashboardsDataStore.timePeriodType)),

        }, {
            key: 'finish_dine_in_actual_amount',
            label: i18n.t('business_analysis_page_text_dine_in_orders_amount'),
            isPrice: true,

        },
    ]
    // 已结 - 外卖
    const closedDeliveryListSource: CardItem[] = [
        {
            key: 'finish_delivery_num',
            label: i18n.t('business_analysis_page_text_takeaways_orders_nums'),

        }, {
            key: 'finish_delivery_actual_amount',
            label: i18n.t('business_analysis_page_text_takeout_amount_total'),
            isPrice: true,

        },
    ]
    // 已结 - 菜品
    const closedDishListSource: CardItem[] = [
        {
            key: 'total_quantity',
            label: i18n.t('business_analysis_page_text_total_dish'),

        }, {
            key: 'total_quantity_rush',
            label: i18n.t('business_analysis_page_text_rush_dish'),

        }, {
            key: 'items',
            label: i18n.t('business_analysis_page_text_cancelled_dish'),

        }, {
            key: 'amount',
            label: i18n.t('business_analysis_page_text_return_dish_amount'),
            isPrice: true,

        }, {
            key: 'reprice_num',
            label: i18n.t('business_analysis_page_text_reprice_num'),

        }, {
            key: 'reprice_amount',
            label: i18n.t('business_analysis_page_text_reprice_amount'),
            isPrice: true,

        }, {
            key: 'refunds_total',
            label: i18n.t('business_analysis_page_text_refunds_total'),

        }, {
            key: 'tip_amount',
            label: i18n.t('business_analysis_page_text_tip'),
            isPrice: true,

        }, {
            key: 'finish_service_amount',
            label: i18n.t('business_analysis_page_text_service_charges'),
            isPrice: true,

        },
    ]

    /** 已结 / 未结分析主卡片下的子区块标题，文案见 locales 中 dashboards_data_section_* */
    const cardArr: TabItem[] = [
        {
            title: i18n.t('business_analysis_page_text_closed_order_analysis'),
            totals: closedListSource,
            id: 2,
            color: 'var(--func-success)',
            items: [
                {
                    title: i18n.t('dashboards_data_section_dine_in'),
                    icon: <DineIcon height={'24px'} color='var(--text-primary-300)' />,
                    items: closedDineInListSource
                },
                {
                    title: i18n.t('dashboards_data_section_takeaway'),
                    icon: <DeliveryIcon height={'24px'} color='var(--text-primary-300)' />,
                    items: closedDeliveryListSource
                },
                {
                    title: i18n.t('dashboards_data_section_dish'),
                    icon: <DishIcon height={'24px'} color='var(--text-primary-300)' />,
                    items: closedDishListSource
                },
            ]
        },
        {
            title: i18n.t('business_analysis_page_text_open_order_analysis'),
            totals: openListSource,
            id: 1,
            color: 'var(--func-info)',
            items: [
                {
                    title: i18n.t('dashboards_data_section_dine_in'),
                    icon: <DineIcon height={'24px'} color='var(--text-primary-300)' />,
                    items: openDineInListSource
                },
                {
                    title: i18n.t('dashboards_data_section_takeaway'),
                    icon: <DeliveryIcon height={'24px'} color='var(--text-primary-300)' />,
                    items: openDeliveryListSource
                },
                {
                    title: i18n.t('dashboards_data_section_dish'),
                    icon: <DishIcon height={'24px'} color='var(--text-primary-300)' />,
                    items: openDishListSource
                },
            ]
        },
    ]

    /** 当前顶部筛选值，与 Store 中的 type 字段保持同步。 */
    const timePeriod = TYPE_TO_PERIOD[dashboardsDataStore.timePeriodType];

    /**
     * 处理顶部时间段切换。
     * 切换后立即刷新三张卡片的数据，保证页面内容同步更新。
     */
    const handlePeriodChange = (value: TimePeriodValue) => {
        dashboardsDataStore.setTimePeriodType(PERIOD_TO_TYPE[value]);
        /** 第三参为 true：仅已结/未结分析区骨架，全页不闪、定时轮询不闪。 */
        dashboardsDataStore.fetchData(stringId, false, true);
    }

    useEffect(() => {
        dashboardsDataStore.fetchData(stringId, true)
    }, [stringId])

    useEffect(() => {
        //  请求失败清除定时器
        if (dashboardsDataStore.errorMessage) {
            interval.current && clearInterval(interval.current)
        } else {
            runInterval();
        }
    }, [dashboardsDataStore.errorMessage])

    useEffect(() => {
        runInterval();
        return () => {
            //  组件卸载清除定时器
            interval.current && clearInterval(interval.current);
        }
    }, [])

    /**
     * 每1min更新一次数据
     */
    const runInterval = () => {
        interval.current && clearInterval(interval.current);
        interval.current = setInterval(() => {
            const currentStringId = mainPageData.currentRestaurant?.string_id || '';
            dashboardsDataStore.fetchData(currentStringId, false);
        }, 60000);
    }



    if (dashboardsDataStore.loading) {
        return <DashboardsDataPageSkeleton />;
    }

    if (dashboardsDataStore.errorMessage) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 500 }}>
                <Typography variant="body1">{dashboardsDataStore.errorMessage}</Typography>
            </Box>
        );
    }


    return (
        <Box
            display="flex"
            flexWrap="wrap"
            gap="24px"
            flexDirection="column"
            sx={{ marginTop: '24px', paddingBottom: '24px' }}
        >

            <Box display="flex" flexDirection="column" gap="16px" alignItems="flex-start" width="100%">
                <TimePeriodFilter value={timePeriod} onChange={handlePeriodChange} />
                <Box
                    sx={mainCardsRowSx}
                    aria-busy={dashboardsDataStore.analysisSectionLoading}
                    aria-label={
                        dashboardsDataStore.analysisSectionLoading
                            ? `${i18n.t('business_analysis_page_text_closed_order_analysis')}, ${i18n.t('business_analysis_page_text_open_order_analysis')}, ${i18n.t('general_text_loading')}`
                            : undefined
                    }
                >
                    <CurrentStatusAndRiskCard store={dashboardsDataStore} cardOuterSx={leftColumnOuterSx} />
                    {dashboardsDataStore.analysisSectionLoading ? (
                        <>
                            <AnalysisMainCardSkeleton
                                titleWidth={220}
                                totalRowCount={2}
                                sectionRowCounts={[4, 2, 9] as const}
                                cardWidth={analysisSkeletonCardWidth}
                            />
                            <AnalysisMainCardSkeleton
                                titleWidth={200}
                                totalRowCount={showUnpaidPaidAmount ? 2 : 1}
                                sectionRowCounts={[4, 2, 4] as const}
                                cardWidth={analysisSkeletonCardWidth}
                            />
                        </>
                    ) : (
                        cardArr.map((cardItem) => (
                            <Card
                                key={cardItem.id}
                                sx={{
                                    padding: '24px 16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                    borderRadius: '16px',
                                    ...analysisCardSx,
                                }}
                            >
                                <Typography variant='h4' sx={{ textAlign: 'center', color: cardItem.color }}>{cardItem.title}</Typography>

                                {
                                    cardItem.totals.map((item) => (
                                        <DataListItem
                                            key={item.key}
                                            icon={item.icon}
                                            isBold={true}
                                            title={item.label}
                                            num={`${item.isPrice ? formatEUR(dashboardsDataStore.data[item.key] as number || 0) : dashboardsDataStore.data[item.key] || '0'}`}
                                        />
                                    ))
                                }
                                {
                                    cardItem.items.map((boxItem, index) => (
                                        <Card
                                            key={index}
                                            sx={{
                                                padding: '16px 8px',
                                                borderRadius: '16px',
                                            }} >
                                            <Box sx={{ mt: 2, mb: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: '8px' }}>
                                                    {boxItem.icon}
                                                    <Typography variant='body1' color='primary'>
                                                        {boxItem.title}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{
                                                    // 子元素之间用横线间隔
                                                    '& > *:not(:last-child)': {
                                                        borderBottom: '1px solid var(--stroke-neutral-800-a25)',
                                                        paddingBottom: '8px',
                                                        marginBottom: '8px'
                                                    }
                                                }}>
                                                    {boxItem.items.map((item) => (
                                                        <DataListItem
                                                            key={item.key}
                                                            title={item.label}
                                                            num={`${item.isPrice ? formatEUR(dashboardsDataStore.data[item.key] as number || 0) : dashboardsDataStore.data[item.key] || '0'}`}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Card>
                                    ))
                                }

                            </Card>
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    )
})

export default DashboardsDataPage;

type TabItem = {
    id: number;
    title: string;
    totals: CardItem[];
    items: CardBox[];
    color: string;
}

type CardBox = {
    title: string;
    // 图标
    icon: JSX.Element;
    items: CardItem[]
}
type CardItem = {
    // 图标
    icon?: JSX.Element;
    // key
    key: keyof CurrentData;
    // 文字
    label: string;
    // 是否金额
    isPrice?: boolean;
}